import { memo, useId } from 'react';
import { Box, BoxProps } from '@mui/material';
import { HeightProperty, WidthProperty } from 'csstype';

type LogoProps = {
  variant: 'wordmark' | 'lockup' | 'brandmark';
  color?: 'main' | 'inverse';
  className?: string;
  width?: WidthProperty<number>;
  height?: HeightProperty<number>;
  sx?: BoxProps['sx'];
};

const Logo = ({
  sx,
  variant,
  className,
  color = 'main',
  width: desiredWidth,
  height: desiredHeight,
}: LogoProps): React.JSX.Element => {
  const id = useId();
  let viewBox: string;
  let width = desiredWidth;
  let height = desiredHeight;
  let hasBrandmark: boolean;
  let hasWordmark: boolean;
  switch (variant) {
    case 'brandmark':
      viewBox = '-2 0 24 32';
      width ??= 32;
      height ??= 32;
      hasBrandmark = true;
      hasWordmark = false;
      break;
    case 'lockup':
      viewBox = '0 0 163 32';
      width ??= 163;
      height ??= 32;
      hasBrandmark = true;
      hasWordmark = true;
      break;
    case 'wordmark':
    default:
      viewBox = '35 0 128 32';
      width ??= 128;
      height ??= 32;
      hasBrandmark = false;
      hasWordmark = true;
      break;
  }
  return (
    <Box
      sx={sx}
      className={className}
      component="svg"
      viewBox={viewBox}
      width={width}
      height={height}
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      role="presentation"
    >
      {hasBrandmark && (
        <radialGradient
          id={id}
          cx="0"
          cy="0"
          gradientTransform="matrix(-4.78786397 6.48485458 -6.48485458 -4.78786397 8.24254 21.2123)"
          gradientUnits="userSpaceOnUse"
          r="1"
        >
          <stop offset="0" stopColor="#007b42" />
          <stop offset="1" stopColor="#19a966" />
        </radialGradient>
      )}
      {hasBrandmark && (
        <path
          d="m8.13713 31.3177c-.00036.3582-.29105.6486-.64926.6533-3.55054.0469-6.506632.3637-7.262708-2.9671-.010132-.0446-.015209-.0896-.01518-.1354.002487-3.9262.242274-13.0945 7.539328-11.0548.23474.0656.40523.266.4138.5096.05645 1.6045-.02028 7.3071-.02598 12.9944z"
          fill={`url(#${id})`}
        />
      )}
      {hasBrandmark && (
        <path
          d="m20.6566 19.9372c.3582-.0028.6519-.2929.6519-.6511l.0001-18.637451c0-.358239-.3069-.648825-.6651-.64668938-23.63604.14090738-20.5478529 7.89796038-20.472763 28.82204038 0-5.9426 8.014663-8.7915 20.485863-8.8868z"
          fill="#1fff78"
        />
      )}
      {hasWordmark && (
        <g fill={color === 'inverse' ? '#fff' : '#001234'}>
          <path d="m35.7229 31.3725c-.3583 0-.6487-.2904-.6487-.6486v-30.075252c0-.358239.2904-.648648.6487-.648648h12.2243c1.9667 0 3.695.403361 5.1849 1.21008 1.5197.77685 2.6967 1.88236 3.5311 3.31653.8642 1.43417 1.2962 3.13726 1.2962 5.10924v.62745c0 1.9421-.4469 3.6452-1.3409 5.1092-.8641 1.4342-2.0561 2.5547-3.5758 3.3614-1.4899.7768-3.1884 1.1653-5.0955 1.1653h-6.3242c-.3583 0-.6487.2904-.6487.6486v10.1761c0 .3582-.2904.6486-.6486.6486zm5.2514-17.5001c0 .3582.2904.6486.6487.6486h5.7431c1.4005 0 2.5329-.3884 3.397-1.1653.8642-.7768 1.2962-1.8375 1.2962-3.182v-.44821c0-1.34454-.432-2.40523-1.2962-3.18207-.8641-.77685-1.9965-1.16527-3.397-1.16527h-5.7431c-.3583 0-.6487.29041-.6487.64865z" />
          <path d="m84.42 32c-2.2051 0-4.1867-.4482-5.9448-1.3445-1.7581-.8964-3.1437-2.1961-4.1569-3.8992-1.0131-1.7031-1.5197-3.7498-1.5197-6.1401v-.717c0-2.3903.5066-4.437 1.5197-6.1401 1.0132-1.7031 2.3988-3.0028 4.1569-3.89916s3.7397-1.34454 5.9448-1.34454 4.1867.44818 5.9448 1.34454 3.1437 2.19606 4.1569 3.89916c1.0131 1.7031 1.5197 3.7498 1.5197 6.1401v.717c0 2.3903-.5066 4.437-1.5197 6.1401-1.0132 1.7031-2.3988 3.0028-4.1569 3.8992-1.7581.8963-3.7397 1.3445-5.9448 1.3445zm0-5.0196c1.7283 0 3.1586-.5528 4.291-1.6583 1.1323-1.1354 1.6985-2.7488 1.6985-4.8403v-.4482c0-2.0915-.5662-3.69-1.6985-4.7955-1.1026-1.1354-2.5329-1.7031-4.291-1.7031-1.7283 0-3.1586.5677-4.291 1.7031-1.1323 1.1055-1.6985 2.704-1.6985 4.7955v.4482c0 2.0915.5662 3.7049 1.6985 4.8403 1.1324 1.1055 2.5627 1.6583 4.291 1.6583z" />
          <path d="m109.78 32c-2.205 0-4.187-.4482-5.945-1.3445-1.758-.8964-3.144-2.1961-4.157-3.8992-1.0132-1.7031-1.5197-3.7498-1.5197-6.1401v-.717c0-2.3903.5065-4.437 1.5197-6.1401 1.013-1.7031 2.399-3.0028 4.157-3.89916s3.74-1.34454 5.945-1.34454 4.186.44818 5.944 1.34454c1.759.89636 3.144 2.19606 4.157 3.89916s1.52 3.7498 1.52 6.1401v.717c0 2.3903-.507 4.437-1.52 6.1401s-2.398 3.0028-4.157 3.8992c-1.758.8963-3.739 1.3445-5.944 1.3445zm0-5.0196c1.728 0 3.158-.5528 4.291-1.6583 1.132-1.1354 1.698-2.7488 1.698-4.8403v-.4482c0-2.0915-.566-3.69-1.698-4.7955-1.103-1.1354-2.533-1.7031-4.291-1.7031-1.729 0-3.159.5677-4.291 1.7031-1.133 1.1055-1.699 2.704-1.699 4.7955v.4482c0 2.0915.566 3.7049 1.699 4.8403 1.132 1.1055 2.562 1.6583 4.291 1.6583z" />
          <path d="m132.196 31.3725c-1.461 0-2.653-.4481-3.576-1.3445-.894-.9262-1.341-2.1512-1.341-3.6751v-11.9003c0-.3583-.291-.6487-.649-.6487h-4.245c-.358 0-.649-.2904-.649-.6486v-3.3638c0-.35823.291-.64864.649-.64864h4.245c.358 0 .649-.29041.649-.64865v-5.60467c0-.35823.29-.64864.648-.64864h4.335c.358 0 .649.29041.649.64865v5.60466c0 .35824.29.64865.648.64865h4.782c.358 0 .649.29041.649.64865v3.36379c0 .3582-.291.6486-.649.6486h-4.782c-.358 0-.648.2904-.648.6487v10.9143c0 .8964.417 1.3446 1.251 1.3446h3.643c.358 0 .648.2904.648.6486v3.3638c0 .3582-.29.6486-.648.6486z" />
          <path d="m151.237 32c-2.205 0-4.187-.4482-5.945-1.3445-1.758-.8964-3.144-2.1961-4.157-3.8992s-1.52-3.7498-1.52-6.1401v-.717c0-2.3903.507-4.437 1.52-6.1401s2.399-3.0028 4.157-3.89916 3.74-1.34454 5.945-1.34454 4.186.44818 5.945 1.34454c1.758.89636 3.143 2.19606 4.156 3.89916 1.014 1.7031 1.52 3.7498 1.52 6.1401v.717c0 2.3903-.506 4.437-1.52 6.1401-1.013 1.7031-2.398 3.0028-4.156 3.8992-1.759.8963-3.74 1.3445-5.945 1.3445zm0-5.0196c1.728 0 3.158-.5528 4.291-1.6583 1.132-1.1354 1.698-2.7488 1.698-4.8403v-.4482c0-2.0915-.566-3.69-1.698-4.7955-1.103-1.1354-2.533-1.7031-4.291-1.7031-1.729 0-3.159.5677-4.291 1.7031-1.133 1.1055-1.699 2.704-1.699 4.7955v.4482c0 2.0915.566 3.7049 1.699 4.8403 1.132 1.1055 2.562 1.6583 4.291 1.6583z" />
          <path d="m65.2898 31.3725c-1.4601 0-2.652-.4481-3.5758-1.3445-.894-.9262-1.3409-2.1512-1.3409-3.6751v-12.549-4.66104-8.49421c0-.358239.2904-.64865.6486-.64865h4.3346c.3582 0 .6487.29041.6487.648649v8.494211 4.66104 11.563c0 .8964.4171 1.3446 1.2515 1.3446h3.6423c.3582 0 .6487.2904.6487.6486v3.3638c0 .3582-.2905.6486-.6487.6486z" />
        </g>
      )}
    </Box>
  );
};

export type { LogoProps };
export default memo(Logo);
